import { Code } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Button } from 'src/components/Button';
import { EquivalentCode } from 'src/components/EquivalentCode';
import { Modal } from 'src/components/Modal';
import { Tab } from 'src/components/Tab';
import {
  ActionPayload,
  ActionType,
  TriggerType,
} from 'src/interfaces/action.interface';
import { actionConfig } from 'src/stubs/action.stub';
import { isValidHttpUrl } from 'src/utils/url';
import { ActionFormContent } from './ActionFormContent';
import { validateActionPhoneNumber } from 'src/utils/telephony';

interface ActionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (form: ActionPayload) => Promise<void>;
  initialForm: ActionPayload;
  selectedId?: string;
  loading?: boolean;
  needsPhoneNumber?: boolean;
  children?: React.ReactNode;
  showRequiredFields?: boolean;
}

export const ActionForm = ({
  isOpen,
  onClose,
  onSubmit,
  initialForm,
  selectedId,
  loading = false,
  needsPhoneNumber = false,
  children,
  showRequiredFields = true,
}: ActionFormProps) => {
  const [form, setForm] = useState<ActionPayload>(initialForm);
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const handleEndpointAction = () => {
    if (selectedId) {
      return `actions/update?id=${selectedId}`;
    } else {
      return 'actions/create';
    }
  };

  const isFieldEmpty = (field: string | undefined) => !field || field === '';

  const isValidForm = (form: ActionPayload) => {
    const { action_trigger, config, type } = form;

    if (action_trigger.type === TriggerType.PhraseBased) {
      const phraseConfig = action_trigger.config as
        | { phrase_triggers?: { phrase: string }[] }
        | undefined;
      if (
        !phraseConfig ||
        !phraseConfig.phrase_triggers ||
        isFieldEmpty(phraseConfig.phrase_triggers[0]?.phrase)
      ) {
        return true;
      }
    }

    if (needsPhoneNumber) {
      const phoneNumber = config.phone_number || '';
      const { isValid } = validateActionPhoneNumber(form.type, phoneNumber);
      if (isFieldEmpty(phoneNumber) || !isValid) {
        return true;
      }
    }

    if (type === ActionType.External) {
      const requiredFields = [
        config.name,
        config.description,
        config.url,
        config.input_schema,
      ];

      if (
        requiredFields.some(isFieldEmpty) ||
        config.speak_on_send === undefined ||
        config.speak_on_receive === undefined ||
        !isValidHttpUrl(String(config.url))
      ) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (
      form.type === ActionType.External &&
      form.config.speak_on_send === undefined
    ) {
      setForm({ ...form, config: { ...form.config, speak_on_send: false } });
    }

    if (
      form.type === ActionType.External &&
      form.config.speak_on_receive === undefined
    ) {
      setForm({ ...form, config: { ...form.config, speak_on_receive: false } });
    }
  }, [form]);

  const handleSubmit = async () => {
    if (needsPhoneNumber) {
      const phoneNumber = form.config.phone_number || '';
      const { isValid, error } = validateActionPhoneNumber(
        form.type,
        phoneNumber,
      );
      if (!isValid) {
        setPhoneNumberError(error);
        return;
      } else {
        setPhoneNumberError(null);
      }
    }
    await onSubmit(form);
  };

  return (
    <Modal
      title={
        <div className="flex items-center">
          <div className="mr-3">
            <img
              src={`/assets/actions/modal/${actionConfig[form.type]?.icon}`}
              alt={actionConfig[form.type]?.label}
              className={clsx(
                'flex select-none pointer-events-none',
                actionConfig[form.type]?.iconStyling,
              )}
            />
          </div>
          {selectedId
            ? `Edit Action - ${actionConfig[form.type]?.label}`
            : form.type === ActionType.External
              ? 'Custom External Actions'
              : actionConfig[form.type]?.label}
        </div>
      }
      isOpen={isOpen}
      onClose={onClose}
      actionButton={
        <Button onClick={handleSubmit} disabled={isValidForm(form) || loading}>
          {selectedId ? 'Save Action' : 'Create Action'}
        </Button>
      }
    >
      <Tab
        items={[
          {
            label: 'Customize',
            content: (
              <>
                {children && (
                  <>
                    <div className="flex justify-end italic text-sm mt-2">
                      * Required fields
                    </div>

                    <div className="bg-neutral-50/65 rounded-md p-4">
                      {children}

                      <ActionFormContent
                        form={form}
                        setForm={setForm}
                        loading={loading}
                        showRequiredFields={showRequiredFields}
                        phoneNumberError={phoneNumberError}
                        setPhoneNumberError={setPhoneNumberError}
                      />
                    </div>
                  </>
                )}

                {!children && (
                  <ActionFormContent
                    form={form}
                    setForm={setForm}
                    loading={loading}
                    showRequiredFields={showRequiredFields}
                    phoneNumberError={phoneNumberError}
                    setPhoneNumberError={setPhoneNumberError}
                  />
                )}
              </>
            ),
          },
          {
            label: (
              <div className="flex items-center">
                <Code className="mr-2" size={20} />
                Show equivalent code
              </div>
            ),
            content: (
              <EquivalentCode
                payload={form}
                endpoint={handleEndpointAction()}
                method="POST"
              />
            ),
          },
        ]}
      />
    </Modal>
  );
};
